import {useCallback} from 'react';
import {useMetrics, useReports} from '@sail/observability';
import {useV1Context} from '@stripe-internal/data-v1';
import current_version from '@stripe-internal/data-v1/schema/version';

import type {UploadedFile} from './FileUploaderFileListItem';
import {useServiceEnvironment} from '../../context/environment/ServiceEnvironmentContext';
import {useApiFetch} from '../../context/fetch/FetchContext';
import {encodeMultipartRequest} from './multipart';
import type {StripeFileFragmentData} from './stripeFileFragment';
import {FileReadError} from '../../utils/errors';
import {useMerchantData} from '../../context';

export const useSendFileUploadRequest = () => {
  const apiFetch = useApiFetch();
  const metrics = useMetrics();
  const {error: reportError} = useReports();
  const {fileEndpoint} = useServiceEnvironment();
  const livemode = useMerchantData()?.livemode;
  const v1Context = useV1Context();
  const {apiKey, accountId} = v1Context;

  const sendFileUploadRequest = useCallback(
    async ({
      file,
      purpose,
      consentedToShareWithPlatform,
    }: {
      file: File;
      purpose: string;
      consentedToShareWithPlatform?: boolean;
    }): Promise<StripeFileFragmentData> => {
      const {name, type} = file;
      let fileData;
      try {
        fileData = await file.arrayBuffer();
      } catch (error) {
        reportError(error, {
          tags: {
            errorLocation: 'Error while reading file array buffer',
          },
        });
        throw new FileReadError('Failed to read array buffer!', error);
      }

      const {contentTypeHeader, data: body} = encodeMultipartRequest({
        purpose,
        file: {
          data: fileData,
          name,
          type,
        },
        consented_to_share_with_platform: !!consentedToShareWithPlatform,
      });

      try {
        metrics.increment('connect.file_upload.request');
        const jsonResponse: StripeFileFragmentData = await apiFetch(
          fileEndpoint,
          {
            method: 'post',
            headers: {
              'Content-Type': contentTypeHeader,
              // if apiKey exists, add it to auth header
              ...(apiKey ? {Authorization: `Bearer ${apiKey}`} : {}),
              // if the key is ephemeral, then we need an explicit version to avoid a 'required_explicit_api_version' error
              ...(apiKey?.startsWith('ek_')
                ? {'Stripe-Version': current_version}
                : {}),
              ...(accountId ? {'Stripe-Account': `${accountId}`} : {}),
              ...(typeof livemode === 'boolean'
                ? {'Stripe-Livemode': `${livemode}`}
                : {}),
            },
            body,
          },
        );
        return jsonResponse;
      } catch (error) {
        metrics.increment('connect.file_upload.error');
        throw error;
      }
    },
    [apiFetch, fileEndpoint, metrics, reportError, apiKey, accountId, livemode],
  );

  return sendFileUploadRequest;
};

export function getTotalFileSize(uploadedFiles: UploadedFile[]): number {
  return uploadedFiles.reduce(
    (acc, uploadedFile) =>
      acc +
      ((uploadedFile.stripeFile?.size ?? uploadedFile.localFile?.size) || 0),
    0,
  );
}

// This is needed so that the caller can distinguish among the list of uploaded
// files as they are added and removed. There is no "ID" on the File object, and
// filename may not be unique.
// Referenced code in dashboard: https://livegrep.corp.stripe.com/view/stripe-internal/pay-server/manage/frontend/src/payments/DisputeEvidenceSubmission/useDisputeEvidenceSubmissionFileUploader.tsx#L18
export function generateUniqueFileID(_fileIndex: number): string {
  return Math.random().toString(36).slice(-6);
}
